<template>
  <div>
    <client-only placeholder="Loading...">
      <p v-if="!sessionReady">Initializing session...</p>
      <p v-else-if="$fetchState.pending">Loading....</p>
      <p v-else-if="$fetchState.error">Error while creating chart</p>
      <div v-else>
        <p>QdtComponent</p>
        <p>Type: {{ type }}</p>
        <p>Props: {{ props }}</p>
      </div>
    </client-only>
  </div>
</template>

<script>
import CustomLineChartSettings from './customLineChartSettings'
import StackedBarchartSettings from './stackedBarchartSettings'

export default {
  name: 'QdtComponent',

  props: {
    type: { type: String, required: true },
    props: { type: Object, required: false, default: () => ({}) },
    minMeasure: { type: Number, required: false, default: undefined },
    maxMeasure: { type: Number, required: false, default: undefined }
  },

  async fetch() {
    if (!process.client) return

    // Nuxt's fetch mixin can invoke fetch() automatically (at beforeMount)
    // before our own mounted() hook has a chance to run, so this needs to be
    // self-contained rather than relying on qdtComponentsLib having already
    // been set elsewhere.
    if (!this.qdtComponentsLib) {
      this.qdtComponentsLib = await import('qdt-components')
    }

    // Settings depend on qdtComponentsLib.picasso, so this has to happen
    // here (not in created(), which always runs before qdtComponentsLib is
    // available).
    if (!this.props.settings) {
      if (this.isStackedBarchart) {
        this.props.settings = StackedBarchartSettings(this.qdtComponentsLib)
      } else if (this.isCustomLineChart) {
        this.props._type = 'customLineChart'
        this.props.type = null
        this.props.settings = CustomLineChartSettings(
          this.qdtComponentsLib,
          this.props.options.measureInfo,
          this.minMeasure,
          this.maxMeasure
        )
      } else {
        this.props.settings = this.qdtComponentsLib.picasso.settings[
          this.props.type
        ]
      }
    }

    this.env = this.$env
    this.qix = this.$qix

    const options = {
      config: {
        host: this.qix.host,
        port: this.qix.port,
        secure: this.qix.secure,
        prefix: this.qix.prefix,
        appId: this.qix.appId,
        withCredentials: true
      },
      connections: {
        vizApi: true,
        engineApi: true
      }
    }

    await this.$qix_session

    this.sessionReady = true

    // Guard against fetch() running more than once (auto-triggered by Nuxt,
    // plus any manual re-invocation) and opening a duplicate engine session.
    if (!this.qdtComponents) {
      this.qdtComponents = new this.qdtComponentsLib.default(
        options.config,
        options.connections
      )
    }
  },
  data() {
    return {
      settings: {},
      qix: null,
      env: null,
      qdtComponents: null,
      qdtComponentsLib: null,
      sessionReady: false
    }
  },

  fetchOnServer: false,

  computed: {
    isStackedBarchart() {
      return this?.props?.type === 'stackedBarchart' || false
    },
    isCustomLineChart() {
      return (
        this?.props?.type === 'customLineChart' ||
        this?.props?._type === 'customLineChart' ||
        false
      )
    }
  },

  mounted() {
    if (!process.client) return
    // fetch() (auto-triggered by Nuxt) runs independently of mounted(); this
    // just needs to wait for it to finish. initClientOnlyComp() already
    // polls/retries until qdtComponents and $el are both ready.
    this.initClientOnlyComp()
  },

  methods: {
    initClientOnlyComp(count = 10) {
      this.$nextTick(() => {
        if (this.qdtComponents && this.$el) {
          const chartProps = { ...this.props, noSelections: true }
          this.qdtComponents.render(this.type, chartProps, this.$el)
        } else if (count > 0) {
          setTimeout(() => this.initClientOnlyComp(count - 1), 100)
        }
      })
    }
  }
}
</script>
